import { FormFieldTypes } from '../report-schedules/constants';
import { UploadOutlined } from '@ant-design/icons';
import {
  Button,
  Checkbox,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Select,
  Typography,
  Upload,
} from 'antd';
import React from 'react';

const { Text } = Typography;

/**
 * Renders a dynamic form based on formFields definitions from a Report Schedule Template.
 * Used when filling out or editing a scheduled report.
 *
 * @param {Object} props
 * @param {Array} props.formFields - Array of field definitions from the template
 * @param {Object} props.formData - Current form data values (field label → value mapping)
 * @param {Function} props.onChange - Called with (fieldId, value) when a field changes
 * @param {boolean} props.readOnly - If true, renders display-only
 * @param {boolean} props.disabled - If true, fields are disabled
 */
function DynamicFormRenderer({
  formFields = [],
  formData = {},
  onChange,
  readOnly = false,
  disabled = false,
}) {
  const sortedFields = [...formFields].sort(
    (a, b) => (a.order || 0) - (b.order || 0),
  );

  if (readOnly) {
    return (
      <div>
        {sortedFields.map((field) => {
          const value = formData[field.fieldId];
          return (
            <div key={field.fieldId} style={{ marginBottom: 12 }}>
              <Text strong>{field.label}</Text>
              <div style={{ marginTop: 4 }}>
                {renderReadOnlyValue(field, value)}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div>
      {sortedFields.map((field) => {
        const value = formData[field.fieldId];
        return (
          <Form.Item
            key={field.fieldId}
            label={field.label}
            required={field.required}
            help={field.helpText}
            style={{ marginBottom: 16 }}
          >
            {renderFormField(field, value, onChange, disabled)}
          </Form.Item>
        );
      })}
    </div>
  );
}

function renderReadOnlyValue(field, value) {
  if (value === undefined || value === null || value === '') {
    return <Text type="secondary">Not provided</Text>;
  }

  switch (field.fieldType) {
    case 'CHECKBOX':
      return <Text>{value ? 'Yes' : 'No'}</Text>;
    case 'MULTI_SELECT':
      return <Text>{Array.isArray(value) ? value.join(', ') : value}</Text>;
    case 'CURRENCY':
      return (
        <Text>
          {typeof value === 'number' ? `$${value.toFixed(2)}` : value}
        </Text>
      );
    case 'FILE_UPLOAD':
      return <Text>{value?.filename || 'File attached'}</Text>;
    default:
      return <Text>{String(value)}</Text>;
  }
}

function renderFormField(field, value, onChange, disabled) {
  const handleChange = (newValue) => {
    if (onChange) onChange(field.fieldId, newValue);
  };

  switch (field.fieldType) {
    case 'TEXT':
      return (
        <Input
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          placeholder={field.placeholder}
          disabled={disabled}
        />
      );
    case 'TEXTAREA':
      return (
        <Input.TextArea
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          placeholder={field.placeholder}
          disabled={disabled}
          rows={4}
        />
      );
    case 'NUMBER':
      return (
        <InputNumber
          value={value}
          onChange={handleChange}
          placeholder={field.placeholder}
          disabled={disabled}
          style={{ width: '100%' }}
        />
      );
    case 'CURRENCY':
      return (
        <InputNumber
          value={value}
          onChange={handleChange}
          placeholder={field.placeholder}
          disabled={disabled}
          style={{ width: '100%' }}
          prefix="$"
          precision={2}
        />
      );
    case 'SELECT':
      return (
        <Select
          value={value}
          onChange={handleChange}
          placeholder={field.placeholder || 'Select an option'}
          disabled={disabled}
          allowClear
        >
          {(field.options || []).map((opt) => (
            <Select.Option key={opt} value={opt}>
              {opt}
            </Select.Option>
          ))}
        </Select>
      );
    case 'MULTI_SELECT':
      return (
        <Select
          mode="multiple"
          value={value || []}
          onChange={handleChange}
          placeholder={field.placeholder || 'Select options'}
          disabled={disabled}
          allowClear
        >
          {(field.options || []).map((opt) => (
            <Select.Option key={opt} value={opt}>
              {opt}
            </Select.Option>
          ))}
        </Select>
      );
    case 'CHECKBOX':
      return (
        <Checkbox
          checked={!!value}
          onChange={(e) => handleChange(e.target.checked)}
          disabled={disabled}
        >
          {field.placeholder || field.label}
        </Checkbox>
      );
    case 'DATE':
      return (
        <DatePicker
          value={
            value ? (typeof value === 'string' ? undefined : value) : undefined
          }
          onChange={(date, dateString) => handleChange(dateString)}
          placeholder={field.placeholder}
          disabled={disabled}
          style={{ width: '100%' }}
        />
      );
    case 'FILE_UPLOAD':
      return (
        <Upload
          beforeUpload={() => false}
          onChange={(info) => handleChange(info.file)}
          disabled={disabled}
          maxCount={1}
        >
          <Button icon={<UploadOutlined />} disabled={disabled}>
            Upload File
          </Button>
        </Upload>
      );
    default:
      return (
        <Input
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          placeholder={field.placeholder}
          disabled={disabled}
        />
      );
  }
}

export default DynamicFormRenderer;
